import { motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";

interface ProgressBarProps {
  currentStep: number;
  completedSteps: number[];
  onStepClick?: (step: number) => void;
}

const TOTAL_STEPS = 6;

const STEP_LABELS = [
  "Your Policy",
  "About You",
  "Your Vehicle",
  "Your Details",
  "Other Drivers",
  "Savings Info",
];

export function ProgressBar({ currentStep, completedSteps, onStepClick }: ProgressBarProps) {
  const prefersReducedMotion = useReducedMotion();
  // Fill ends at the center of the active circle (desktop) / mid-current step (mobile).
  const progressPct = ((currentStep - 0.5) / TOTAL_STEPS) * 100;
  const currentLabel = STEP_LABELS[currentStep - 1] ?? "";

  return (
    <div
      className="sticky top-0 z-50 w-full backdrop-blur-sm"
      style={{ backgroundColor: "rgba(247, 248, 241, 0.85)" }}
    >
      <div className="max-w-form mx-auto px-4 sm:px-0 pt-2 pb-2 sm:pb-1">
        {/* Mobile: compact "Step X of N: Label" row above the thin bar. */}
        <div className="sm:hidden flex items-baseline justify-between text-xs mb-1.5" style={{ color: "var(--text-muted)" }}>
          <span>
            <span className="font-semibold" style={{ color: "var(--text-primary)" }}>
              {currentLabel}
            </span>
            <span className="ml-2">Step {currentStep} of {TOTAL_STEPS}</span>
          </span>
          <button
            type="button"
            onClick={() => currentStep > 1 && onStepClick?.(currentStep - 1)}
            disabled={currentStep <= 1}
            className="text-xs py-1 px-2 rounded disabled:opacity-0"
            style={{ color: "var(--text-muted)" }}
            aria-label="Go to previous step"
          >
            ← Back
          </button>
        </div>

        {/* Progress track, shared between mobile and desktop. */}
        <div
          className="w-full h-[3px] rounded-full"
          style={{ backgroundColor: "rgba(142, 142, 142, 0.18)" }}
        >
          <motion.div
            className="h-full rounded-full bg-brand-primary"
            initial={{ width: 0 }}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.3, ease: [0.16, 1, 0.3, 1] }}
          />
        </div>

        {/* Desktop: step circles + labels. Hidden on mobile (<640px). */}
        <div className="mt-2 hidden sm:flex items-start">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => {
            const step = i + 1;
            const isCompleted = completedSteps.includes(step) && step !== currentStep;
            const isCurrent = step === currentStep;
            const isClickable = step <= currentStep;

            return (
              <div key={step} className="flex-1 flex flex-col items-center gap-1.5">
                <button
                  type="button"
                  disabled={!isClickable}
                  onClick={() => isClickable && onStepClick?.(step)}
                  aria-label={`${STEP_LABELS[i]}${
                    isCurrent ? ' (current)' : isCompleted ? ' (completed)' : ''
                  }`}
                  aria-current={isCurrent ? 'step' : undefined}
                  className="flex items-center justify-center rounded-full text-xs font-medium transition-all"
                  style={{
                    width: 26,
                    height: 26,
                    cursor: isClickable ? 'pointer' : 'default',
                    backgroundColor: isCurrent || isCompleted
                      ? 'var(--color-primary)'
                      : 'transparent',
                    color: isCurrent || isCompleted ? '#fff' : 'var(--text-muted)',
                    border: isCurrent || isCompleted
                      ? 'none'
                      : '1.5px solid var(--text-muted)',
                    boxShadow: isCurrent ? '0 0 0 4px rgba(140, 213, 95, 0.25)' : 'none',
                  }}
                >
                  {isCompleted ? <Check size={14} strokeWidth={3} /> : step}
                </button>

                <span
                  className="text-xs leading-tight text-center"
                  style={{
                    color: isCurrent ? 'var(--text-primary)' : 'var(--text-muted)',
                    fontWeight: isCurrent ? 600 : 400,
                    maxWidth: 84,
                  }}
                >
                  {STEP_LABELS[i]}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
